import { useState, useCallback, useMemo } from "react";
import { Alert, Portal, Snackbar } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import WarningRoundedIcon from "@mui/icons-material/WarningRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const useToast = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState("Error Occurred.");
  const [alertType, setAlertType] = useState("error");
  const [toastDuration, setToastDuration] = useState(6000);
  const [container, setContainer] = useState(null);
  const [toastAction, setToastAction] = useState(null);


  // Show Toast
  const showToast = useCallback(
    (message, alertType = "error", container = null, toastDuration = 6000, action= null) => {
      setMessage(message);
      setAlertType(alertType);
      setToastDuration(toastDuration);
      setIsOpen(true);
      setToastAction(action);
      // Only update container if it's actually different
      setContainer((prev) => (prev !== container ? container : prev));
    },
    []
  );

  // Close Toast
  const closeToast = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Handle Close
  const handleToastClose = useCallback(
    (event, reason) => {
      if (reason === "clickaway") return;
      closeToast();
    },
    [closeToast]
  );

  const alertColors = {
    success: { backgroundColor: "#D3FCD2", color: "#065E49" },
    error: { backgroundColor: "#FFE9D5", color: "#7A0916" },
    warning: { backgroundColor: "#FFF7E6", color: "#7A4100" },
    info: { backgroundColor: "#E6FCFF", color: "#003768" },
  };

  const alertIcons = {
    success: <CheckCircleIcon sx={{ color: "#22C55E" }} />,
    error: <ErrorIcon sx={{ color: "#FF5630" }} />,
    warning: <WarningRoundedIcon sx={{ color: "#FFAB00" }} />,
    info: <InfoOutlinedIcon sx={{ color: "#00B8D9" }} />,
  };

  // Memoized Toast Component
  const Toast = useMemo(() => {
    return () => (
      <Portal container={container}>
        <Snackbar
          open={isOpen}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          autoHideDuration={toastDuration}
          onClose={(event, reason) => handleToastClose(event, reason)}
          sx={{ marginTop: "60px" }}
        >
          <Alert
            onClose={handleToastClose}
            severity={alertType.toLowerCase()}
            variant="standard"
            sx={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              backgroundColor:
                alertColors[alertType]?.backgroundColor || "inherit",
              color: alertColors[alertType]?.color || "inherit",
              "& .MuiAlert-message": {
                padding: "8px 0",
              },
              "& .MuiAlert-action": {
                padding: "0 0 0 16px",
                alignSelf: "center",
              },
            }}
            icon={alertIcons[alertType]}
            action={toastAction}
          >
            {message}
          </Alert>
        </Snackbar>
      </Portal>
    );
  }, [isOpen, message, alertType, toastDuration, container, handleToastClose, toastAction]);

  return {
    Toast,
    showToast,
    closeToast,
  };
};

export default useToast;
